import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import request from '../utils/request';

const STATUS_LABELS = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  needs_info: 'Needs Info', approved: 'Approved', rejected: 'Rejected',
  suspended: 'Suspended', deactivated: 'Deactivated',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600', submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-indigo-100 text-indigo-700', needs_info: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
  suspended: 'bg-red-100 text-red-700', deactivated: 'bg-gray-100 text-gray-500',
};

const ACTIONS = {
  submitted: [{ status: 'under_review', label: 'Start Review', color: 'bg-indigo-600 text-white' }],
  under_review: [
    { status: 'approved', label: 'Approve', color: 'bg-green-600 text-white' },
    { status: 'needs_info', label: 'Request Info', color: 'bg-yellow-500 text-white' },
    { status: 'rejected', label: 'Reject', color: 'bg-red-600 text-white' },
  ],
  needs_info: [{ status: 'under_review', label: 'Resume Review', color: 'bg-indigo-600 text-white' }],
  approved: [
    { status: 'suspended', label: 'Suspend', color: 'bg-red-600 text-white' },
    { status: 'deactivated', label: 'Deactivate', color: 'bg-gray-600 text-white' },
  ],
  suspended: [
    { status: 'approved', label: 'Reinstate', color: 'bg-green-600 text-white' },
    { status: 'deactivated', label: 'Deactivate', color: 'bg-gray-600 text-white' },
  ],
};

export default function ProviderDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');

  const fetchProvider = useCallback(async () => {
    try {
      const { data } = await request.get(`/admin/providers/${id}`);
      setProvider(data.provider);
      setNotes(data.provider.reviewNotes || '');
    } catch (err) {
      console.error('Failed to fetch provider:', err);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchProvider(); }, [fetchProvider]);

  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`Change status to "${STATUS_LABELS[newStatus]}"?`)) return;
    setActionLoading(true);
    try {
      await request.patch(`/admin/providers/${id}/status`, { status: newStatus, reason });
      setReason('');
      await fetchProvider();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
    setActionLoading(false);
  };

  const handleSaveNotes = async () => {
    try {
      await request.patch(`/admin/providers/${id}/notes`, { reviewNotes: notes });
      alert('Notes saved');
    } catch (err) {
      alert('Failed to save notes');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="flex-1 p-6">
        <p className="text-zinc-500">Provider not found</p>
      </div>
    );
  }

  const actions = ACTIONS[provider.status] || [];

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/providers')} className="text-sm text-indigo-600 hover:text-indigo-800 mb-4">
          &larr; Back to Queue
        </button>

        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">{provider.firstName} {provider.lastName}</h1>
            <p className="text-zinc-500">{provider.email}</p>
          </div>
          <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${STATUS_COLORS[provider.status]}`}>
            {STATUS_LABELS[provider.status]}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Section title="Business Info">
            <Field label="Business Name" value={provider.businessName} />
            <Field label="Description" value={provider.businessDescription} />
            <Field label="Website" value={provider.websiteUrl} />
            <Field label="Phone" value={provider.phoneNumber} />
          </Section>

          <Section title="Capabilities">
            <Field
              label="Service Categories"
              value={provider.serviceCategories?.map((sc) => sc.serviceCategoryId?.displayName || sc.serviceCategoryId?.name).filter(Boolean).join(', ')}
            />
            <Field
              label="Markets"
              value={provider.markets?.map((m) => `${m.marketId?.name || 'Unknown'}${m.isActive ? '' : ' (inactive)'}`).join(', ')}
            />
            <Field label="Max Concurrent Jobs" value={String(provider.maxConcurrentJobs)} />
          </Section>
        </div>

        {provider.documents?.length > 0 && (
          <Section title="Documents" className="mb-8">
            <div className="space-y-2">
              {provider.documents.map((doc, i) => (
                <div key={doc._id || i} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium text-zinc-700">{doc.label || doc.type}</span>
                    <span className="ml-2 text-xs text-zinc-400 uppercase">{doc.type}</span>
                  </div>
                  {doc.expiresAt && (
                    <span className="text-xs text-zinc-400">Expires: {new Date(doc.expiresAt).toLocaleDateString()}</span>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {provider.infoRequests?.length > 0 && (
          <Section title="Information Requests" className="mb-8">
            <div className="space-y-2">
              {provider.infoRequests.map((ir, i) => (
                <div key={i} className={`p-3 rounded-lg ${ir.resolvedAt ? 'bg-green-50' : 'bg-yellow-50'}`}>
                  <div className="text-sm font-medium">{ir.field}: {ir.message}</div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Requested: {new Date(ir.requestedAt).toLocaleDateString()}
                    {ir.resolvedAt && ` | Resolved: ${new Date(ir.resolvedAt).toLocaleDateString()}`}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {provider.statusHistory?.length > 0 && (
          <Section title="Status History" className="mb-8">
            <div className="space-y-2">
              {[...provider.statusHistory].reverse().map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_COLORS[h.status] || 'bg-gray-100'}`}>
                    {STATUS_LABELS[h.status] || h.status}
                  </span>
                  <span className="text-zinc-500">{new Date(h.changedAt).toLocaleString()}</span>
                  {h.reason && <span className="text-zinc-400">- {h.reason}</span>}
                </div>
              ))}
            </div>
          </Section>
        )}

        <Section title="Internal Review Notes" className="mb-8">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full border border-zinc-200 rounded-lg p-3 text-sm resize-y min-h-[100px] focus:outline-none focus:ring-2 focus:ring-indigo-300"
            placeholder="Internal notes about this provider..."
          />
          <button onClick={handleSaveNotes} className="mt-2 px-4 py-2 bg-zinc-800 text-white text-sm font-medium rounded-lg hover:bg-zinc-700">
            Save Notes
          </button>
        </Section>

        {actions.length > 0 && (
          <Section title="Actions" className="mb-8">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              className="w-full border border-zinc-200 rounded-lg p-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
            <div className="flex gap-3 flex-wrap">
              {actions.map((a) => (
                <button
                  key={a.status}
                  onClick={() => handleStatusChange(a.status)}
                  disabled={actionLoading}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-opacity disabled:opacity-50 ${a.color}`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, className = '' }) {
  return (
    <div className={`bg-white rounded-2xl border border-zinc-200 p-6 ${className}`}>
      <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{label}</div>
      <div className="text-sm text-zinc-800 mt-0.5">{value || '\u2014'}</div>
    </div>
  );
}
